import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, finalize, map, Observable, of, shareReplay, timeout } from 'rxjs';
import { environment } from 'src/environments/environment';

const githubRequestTimeoutMs = 15000;

const clearScapeAPIBaseUrl = (environment.name === 'production')
  ? 'https://api.clearscape.teradata.com'
  : (environment.name === 'ci')
  ? 'https://api.ci.clearscape.teradata.com'
  : `https://api.${environment.name}.clearscape.teradata.com`;

export interface Demo {
  id: number;
  demo_title: string;
  short_description: string | null;
  date_added: string;
  available: 'Y' | 'N' | 'P';
  demo_type: string;
  follow_up: string | null;
}

export interface DemoFile {
  id: number;
  demo_type: 'nbviewer' | 'notebook';
  directory: string;
  filename: string;
  programming_language: string | null;
  label: string;
  author: string | null;
  curator: string | null;
  Language: string | null;
}

export interface TopicArea {
  id: number;
  topic_area_title: string;
  sequence: number | null;
}

export interface Topic {
  id: number;
  topic_title: string;
  sequence: number | null;
  topic_area: number | null;
}

export interface DemoTopic {
  demo_id: number;
  topic_id: number;
  sequence: number | null;
}

export interface BootstrapData {
  demos: Demo[];
  demoFiles: DemoFile[];
  topicAreas: TopicArea[];
  topics: Topic[];
  demoTopics: DemoTopic[];
}

export interface GitHubFile {
  path: string;
  name: string;
}

export interface GitHubPR {
  number: number;
  title: string;
  html_url: string;
}

export interface AdItem {
  image: string;
  link: string;
  deletable: boolean;
  type?: 'image' | 'video';
  thumbnail?: string;
}


export interface SpecialEvent {
  event_id: number;
  sponsor_email: string | null;
  event_name: string | null;
  description: string | null;
  country: string | null;
  city: string | null;
  scheduled_start_dt: string | null;
  scheduled_end_dt: string | null;
  start_ts: string | null;
  end_ts: string | null;
  sponsoring_td_org: string | null;
}

export type SpecialEventAttendeeType = 'A' | 'S' | 'O';

export interface SpecialEventAttendee {
  event_id: number;
  email: string | null;
  event_attendee_name: string | null;
  company: string | null;
  start_ts: string | null;
  end_ts: string | null;
  attendee_type: SpecialEventAttendeeType | null;
}

export interface SpecialEventUserSuggestion {
  email: string;
  name: string;
  company: string | null;
}

export interface SpecialEventAttendeeImportResult {
  inserted: number;
  updated: number;
  skipped: number;
  errors: string[];
}

export type UserMaintenanceType = 'Suspend' | 'Purge' | 'PurgeBlacklist' | 'Re-Enable';

export interface PrismaAlert {
  id: string;
  name: string;
  summary: string;
  resource: string;
  userEmail: string;
  severity: string;
  state: string;
  startedAt: string | null;
  closed?: boolean;
  closedAt?: string | null;
  userSuspended?: boolean;
  policyName: string;
  category?: string;
  container?: string;
  image?: string;
  containerUser?: string;
  rawLog?: string;
  suggestion?: string | null;
  labels: Record<string, string>;
}

export interface NotebookUsage {
  name: string;
  openCount: number;
  lastOpened: string;
}

export interface EnvironmentNotebooks {
  environment: string;
  state: string;
  reachable: boolean;
  message?: string;
  jupyterUrl?: string;
  notebooks: NotebookUsage[];
}

export interface UsageRecord {
  machineName: string;
  machineNo: number;
  activityDate: string;
  usageHours: number;
  cpuCount: number;
  deleted: boolean;
}

export interface UserUsage {
  billingMonthBegin: string | null;
  budgetCredits: number | null;
  totalUsageHours: number;
  records: UsageRecord[];
}

export interface UserItem {
  id: string;
  email: string;
  firstname?: string;
  lastname?: string;
  company?: string;
  title?: string;
  country?: string;
  role?: string;
  disabled?: boolean;
  currentConsumption?: number | string;
  maxCredits?: number | string;
  profileType?: string;
  emailMarketingConsent?: boolean;
  termsAndConditionsConsent?: boolean;
  resetDate?: unknown;
  lastResetDate?: unknown;
}

export interface UserListPage {
  items: UserItem[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export type UserListFilterField = 'email' | 'id' | 'company' | 'role' | 'status' | 'profileType';

export interface AdminEnvironment {
  name: string;
  state: string;
  region: string;
  owner: string;
  createDate: string;
  oafState?: string;
  dnsName?: string;
  size?: string;
  zoneName?: string;
}

export interface EnvironmentListPage {
  items: AdminEnvironment[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export type EnvironmentListFilterField = 'name' | 'owner' | 'status' | 'region';

export type EnvironmentOperation = 'stop' | 'start' | 'terminate' | 'delete' | 'restart' | 'stopandfail';

export interface EnvironmentOperationStatus {
  environment: string;
  owner: string;
  operation: EnvironmentOperation;
  status: 'ok' | 'failed';
  reason?: string;
}

export interface EnvironmentCredential {
  name: string;
  value: string;
}

export interface EnvironmentService {
  name: string;
  url: string;
  credentials: EnvironmentCredential[];
}

export interface EnvironmentDetail {
  name: string;
  state: string;
  region: string;
  owner?: string;
  ownerId?: string;
  ip?: string;
  dnsName?: string;
  webDnsName?: string;
  zone?: string;
  zoneName?: string;
  type?: string;
  size?: string;
  oafState?: string;
  createDate?: string;
  startingTime?: string;
  startedTime?: string;
  stoppingTime?: string;
  stoppedTime?: string;
  services?: EnvironmentService[];
  startupScript?: string;
  startupScriptResult?: { stdout?: string; stderr?: string };
}

export interface EnvironmentEvent {
  type: string;
  timestamp: string | number;
  initiator: 'USER' | 'SYSTEM';
  actorId: string | null;
  actorEmail: string | null;
  details: Record<string, unknown> | null;
  severity: 'INFO' | 'WARNING' | 'ERROR';
}

@Injectable({
  providedIn: 'root'
})
export class AdminApiService {
  private readonly githubCacheTtlMs = {
    files: 120000,
    pullRequests: 30000,
    prFiles: 60000,
  };

  private readonly githubCache = new Map<string, { expiresAt: number; value: any }>();
  private readonly githubInFlight = new Map<string, Observable<any>>();

  constructor(private http: HttpClient) {}

  private getCachedValue<T>(cacheKey: string): T | null {
    const entry = this.githubCache.get(cacheKey);
    if (!entry) {
      return null;
    }

    if (Date.now() >= entry.expiresAt) {
      this.githubCache.delete(cacheKey);
      return null;
    }

    return entry.value as T;
  }

  private setCachedValue<T>(cacheKey: string, value: T, ttlMs: number): void {
    this.githubCache.set(cacheKey, {
      value,
      expiresAt: Date.now() + ttlMs,
    });
  }

  private getOrCreateGitHubRequest<T>(cacheKey: string, ttlMs: number, requestFactory: () => Observable<T>): Observable<T> {
    const cached = this.getCachedValue<T>(cacheKey);
    if (cached !== null) {
      return new Observable<T>((subscriber) => {
        subscriber.next(cached);
        subscriber.complete();
      });
    }

    const existingInFlight = this.githubInFlight.get(cacheKey) as Observable<T> | undefined;
    if (existingInFlight) {
      return existingInFlight;
    }

    const request$ = requestFactory().pipe(
      timeout(githubRequestTimeoutMs),
      map((value) => {
        this.setCachedValue(cacheKey, value, ttlMs);
        return value;
      }),
      catchError((error) => {
        console.error(`GitHub request failed for ${cacheKey}:`, error);
        return of([] as unknown as T);
      }),
      finalize(() => this.githubInFlight.delete(cacheKey)),
      shareReplay(1),
    );

    this.githubInFlight.set(cacheKey, request$);

    return request$;
  }

  private authHeaders(token: string): HttpHeaders {
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });
  }

  private unwrapResponse<T>(response: any): T {
    if (response && typeof response === 'object' && 'success' in response) {
      return response.data as T;
    }
    return response as T;
  }

  bootstrap(token: string): Observable<BootstrapData> {
    return this.http
      .get(`${clearScapeAPIBaseUrl}/admin/bootstrap`, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<BootstrapData>(response)));
  }

  listDemos(token: string): Observable<Demo[]> {
    return this.http
      .get(`${clearScapeAPIBaseUrl}/admin/demos`, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<Demo[]>(response) || []));
  }

  createDemo(token: string, payload: Partial<Demo>): Observable<Demo> {
    return this.http
      .post(`${clearScapeAPIBaseUrl}/admin/demos`, payload, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<Demo>(response)));
  }

  updateDemo(token: string, id: number, payload: Partial<Demo>): Observable<Demo> {
    return this.http
      .put(`${clearScapeAPIBaseUrl}/admin/demos/${id}`, payload, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<Demo>(response)));
  }

  deleteDemo(token: string, id: number): Observable<any> {
    return this.http.delete(`${clearScapeAPIBaseUrl}/admin/demos/${id}`, { headers: this.authHeaders(token) });
  }

  listDemoFiles(token: string): Observable<DemoFile[]> {
    return this.http
      .get(`${clearScapeAPIBaseUrl}/admin/demo-files`, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<DemoFile[]>(response) || []));
  }

  createDemoFile(token: string, payload: Partial<DemoFile>): Observable<DemoFile> {
    return this.http
      .post(`${clearScapeAPIBaseUrl}/admin/demo-files`, payload, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<DemoFile>(response)));
  }

  updateDemoFile(token: string, id: number, label: string, payload: Partial<DemoFile>): Observable<DemoFile> {
    return this.http
      .put(`${clearScapeAPIBaseUrl}/admin/demo-files/${id}/${encodeURIComponent(label)}`, payload, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<DemoFile>(response)));
  }

  deleteDemoFile(token: string, id: number, label: string): Observable<any> {
    return this.http.delete(`${clearScapeAPIBaseUrl}/admin/demo-files/${id}/${encodeURIComponent(label)}`, {
      headers: this.authHeaders(token),
    });
  }

  listTopicAreas(token: string): Observable<TopicArea[]> {
    return this.http
      .get(`${clearScapeAPIBaseUrl}/admin/topic-areas`, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<TopicArea[]>(response) || []));
  }

  createTopicArea(token: string, payload: Partial<TopicArea>): Observable<TopicArea> {
    return this.http
      .post(`${clearScapeAPIBaseUrl}/admin/topic-areas`, payload, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<TopicArea>(response)));
  }

  updateTopicArea(token: string, id: number, payload: Partial<TopicArea>): Observable<TopicArea> {
    return this.http
      .put(`${clearScapeAPIBaseUrl}/admin/topic-areas/${id}`, payload, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<TopicArea>(response)));
  }

  deleteTopicArea(token: string, id: number): Observable<any> {
    return this.http.delete(`${clearScapeAPIBaseUrl}/admin/topic-areas/${id}`, { headers: this.authHeaders(token) });
  }

  listTopics(token: string): Observable<Topic[]> {
    return this.http
      .get(`${clearScapeAPIBaseUrl}/admin/topics`, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<Topic[]>(response) || []));
  }

  createTopic(token: string, payload: Partial<Topic>): Observable<Topic> {
    return this.http
      .post(`${clearScapeAPIBaseUrl}/admin/topics`, payload, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<Topic>(response)));
  }

  updateTopic(token: string, id: number, payload: Partial<Topic>): Observable<Topic> {
    return this.http
      .put(`${clearScapeAPIBaseUrl}/admin/topics/${id}`, payload, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<Topic>(response)));
  }

  deleteTopic(token: string, id: number): Observable<any> {
    return this.http.delete(`${clearScapeAPIBaseUrl}/admin/topics/${id}`, { headers: this.authHeaders(token) });
  }

  listDemoTopics(token: string): Observable<DemoTopic[]> {
    return this.http
      .get(`${clearScapeAPIBaseUrl}/admin/demo-topics`, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<DemoTopic[]>(response) || []));
  }

  createDemoTopic(token: string, payload: Partial<DemoTopic>): Observable<DemoTopic> {
    return this.http
      .post(`${clearScapeAPIBaseUrl}/admin/demo-topics`, payload, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<DemoTopic>(response)));
  }

  updateDemoTopic(token: string, demoId: number, topicId: number, payload: Partial<DemoTopic>): Observable<DemoTopic> {
    return this.http
      .put(`${clearScapeAPIBaseUrl}/admin/demo-topics/${demoId}/${topicId}`, payload, {
        headers: this.authHeaders(token),
      })
      .pipe(map((response) => this.unwrapResponse<DemoTopic>(response)));
  }

  deleteDemoTopic(token: string, demoId: number, topicId: number): Observable<any> {
    return this.http.delete(`${clearScapeAPIBaseUrl}/admin/demo-topics/${demoId}/${topicId}`, {
      headers: this.authHeaders(token),
    });
  }

  fetchGitHubJupyterDemosFiles(): Observable<GitHubFile[]> {
    const treeUrl = 'https://api.github.com/repos/Teradata/jupyter-demos/git/trees/main?recursive=1';
    return this.getOrCreateGitHubRequest('github-jupyter-demos-files', this.githubCacheTtlMs.files, () =>
      this.http.get<any>(treeUrl).pipe(
        map((response) => {
          const files: GitHubFile[] = [];
          (response.tree || []).forEach((item: any) => {
            if (item.type === 'blob' && item.path.endsWith('.ipynb')) {
              files.push({
                path: item.path,
                name: item.path.split('/').pop() || '',
              });
            }
          });
          return files;
        }),
      )
    );
  }

  private extractIpynbFiles(items: any[], basePath: string): GitHubFile[] {
    const files: GitHubFile[] = [];

    items.forEach((item) => {
      const fullPath = basePath ? `${basePath}/${item.name}` : item.name;

      if (item.type === 'file' && item.name.endsWith('.ipynb')) {
        files.push({
          path: fullPath,
          name: item.name,
        });
      }
    });

    return files;
  }

  getPendingGitHubFiles(githubFiles: GitHubFile[], existingDemoFiles: DemoFile[]): GitHubFile[] {
    const existingPaths = new Set(existingDemoFiles.map(df => `${df.directory}/${df.filename}`));
    return githubFiles.filter(gf => !existingPaths.has(gf.path));
  }

  fetchGitHubPullRequests(): Observable<GitHubPR[]> {
    return this.getOrCreateGitHubRequest('github-open-pull-requests', this.githubCacheTtlMs.pullRequests, () =>
      this.http
        .get<any[]>('https://api.github.com/repos/Teradata/jupyter-demos/pulls?state=open&per_page=50')
        .pipe(
          map((prs) =>
            (prs || []).map((pr) => ({
              number: pr.number,
              title: pr.title,
              html_url: pr.html_url,
            }))
          )
        )
    );
  }

  fetchGitHubPRFiles(prNumber: number): Observable<GitHubFile[]> {
    const cacheKey = `github-pr-files:${prNumber}`;
    return this.getOrCreateGitHubRequest(cacheKey, this.githubCacheTtlMs.prFiles, () =>
      this.http
        .get<any[]>(`https://api.github.com/repos/Teradata/jupyter-demos/pulls/${prNumber}/files?per_page=100`)
        .pipe(
          map((files) => {
            const result: GitHubFile[] = [];
            (files || []).forEach((f) => {
              if (f.filename && f.filename.endsWith('.ipynb') && f.status !== 'removed') {
                result.push({
                  path: f.filename,
                  name: f.filename.split('/').pop() || '',
                });
              }
            });
            return result;
          })
        )
    );
  }

  listAds(token: string): Observable<AdItem[]> {
    return this.http
      .get(`${clearScapeAPIBaseUrl}/admin/ads`, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<AdItem[]>(response) || []));
  }

  addAdByUrl(token: string, image: string, link: string, type: 'image' | 'video' = 'image', thumbnail?: string): Observable<AdItem[]> {
    const body: { image: string; link: string; type: 'image' | 'video'; thumbnail?: string } = { image, link, type };
    if (type === 'video' && thumbnail) {
      body.thumbnail = thumbnail;
    }
    return this.http
      .post(`${clearScapeAPIBaseUrl}/admin/ads`, body, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<AdItem[]>(response) || []));
  }

  uploadAd(token: string, file: File, link: string, type: 'image' | 'video' = 'image', thumbnailFile?: File): Observable<AdItem[]> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('link', link);
    formData.append('type', type);
    if (type === 'video' && thumbnailFile) {
      formData.append('thumbnail', thumbnailFile, thumbnailFile.name);
    }
    return this.http
      .post(`${clearScapeAPIBaseUrl}/admin/ads/upload`, formData, {
        headers: new HttpHeaders({ Authorization: `Bearer ${token}` }),
      })
      .pipe(map((response) => this.unwrapResponse<AdItem[]>(response) || []));
  }

  deleteAd(token: string, image: string): Observable<AdItem[]> {
    return this.http
      .request('delete', `${clearScapeAPIBaseUrl}/admin/ads`, {
        headers: this.authHeaders(token),
        body: { image },
      })
      .pipe(map((response) => this.unwrapResponse<AdItem[]>(response) || []));
  }

  listSpecialEvents(token: string, search?: string): Observable<SpecialEvent[]> {
    const query = search && search.trim() ? `?search=${encodeURIComponent(search.trim())}` : '';
    return this.http
      .get(`${clearScapeAPIBaseUrl}/admin/special-events${query}`, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<SpecialEvent[]>(response) || []));
  }

  createSpecialEvent(token: string, payload: Partial<SpecialEvent>): Observable<SpecialEvent> {
    return this.http
      .post(`${clearScapeAPIBaseUrl}/admin/special-events`, payload, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<SpecialEvent>(response)));
  }

  updateSpecialEvent(token: string, eventId: number, payload: Partial<SpecialEvent>): Observable<SpecialEvent> {
    return this.http
      .put(`${clearScapeAPIBaseUrl}/admin/special-events/${eventId}`, payload, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<SpecialEvent>(response)));
  }

  deleteSpecialEvent(token: string, eventId: number): Observable<any> {
    return this.http.delete(`${clearScapeAPIBaseUrl}/admin/special-events/${eventId}`, { headers: this.authHeaders(token) });
  }

  listSpecialEventAttendees(token: string, eventId: number, search?: string): Observable<SpecialEventAttendee[]> {
    const query = search && search.trim() ? `?search=${encodeURIComponent(search.trim())}` : '';
    return this.http
      .get(`${clearScapeAPIBaseUrl}/admin/special-events/${eventId}/attendees${query}`, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<SpecialEventAttendee[]>(response) || []));
  }

  createSpecialEventAttendee(token: string, eventId: number, payload: Partial<SpecialEventAttendee>): Observable<SpecialEventAttendee> {
    return this.http
      .post(`${clearScapeAPIBaseUrl}/admin/special-events/${eventId}/attendees`, payload, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<SpecialEventAttendee>(response)));
  }

  updateSpecialEventAttendee(token: string, eventId: number, email: string, payload: Partial<SpecialEventAttendee>): Observable<SpecialEventAttendee> {
    return this.http
      .put(`${clearScapeAPIBaseUrl}/admin/special-events/${eventId}/attendees/${encodeURIComponent(email)}`, payload, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<SpecialEventAttendee>(response)));
  }

  deleteSpecialEventAttendee(token: string, eventId: number, email: string): Observable<any> {
    return this.http.delete(`${clearScapeAPIBaseUrl}/admin/special-events/${eventId}/attendees/${encodeURIComponent(email)}`, {
      headers: this.authHeaders(token),
    });
  }

  searchSpecialEventUsers(token: string, search?: string): Observable<SpecialEventUserSuggestion[]> {
    const query = search && search.trim() ? `?search=${encodeURIComponent(search.trim())}` : '';
    return this.http
      .get(`${clearScapeAPIBaseUrl}/admin/special-events/user-suggestions${query}`, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<SpecialEventUserSuggestion[]>(response) || []));
  }

  bulkUploadSpecialEventAttendees(token: string, eventId: number, file: File): Observable<SpecialEventAttendeeImportResult> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http
      .post(`${clearScapeAPIBaseUrl}/admin/special-events/${eventId}/attendees/bulk`, formData, {
        headers: new HttpHeaders({ Authorization: `Bearer ${token}` }),
      })
      .pipe(map((response) => this.unwrapResponse<SpecialEventAttendeeImportResult>(response)));
  }

  listUsers(token: string, options?: { page?: number; pageSize?: number; filterField?: UserListFilterField; filterValue?: string }): Observable<UserListPage> {
    let params = new HttpParams();
    const page = options?.page ?? 1;
    const pageSize = options?.pageSize ?? 20;
    const filterField = options?.filterField;
    const filterValue = options?.filterValue?.trim();

    if (typeof page === 'number' && Number.isFinite(page)) {
      params = params.set('page', String(page));
    }

    if (typeof pageSize === 'number' && Number.isFinite(pageSize)) {
      params = params.set('pageSize', String(pageSize));
    }

    if (filterField && filterValue) {
      params = params.set('filterField', filterField);
      params = params.set('filterValue', filterValue);
    }

    return this.http
      .get(`${clearScapeAPIBaseUrl}/admin/users`, { headers: this.authHeaders(token), params })
      .pipe(map((response) => this.normalizeUserListResponse(response, page, pageSize, filterField, filterValue)));
  }

  private normalizeUserListResponse(
    response: any,
    requestedPage: number,
    requestedPageSize: number,
    filterField?: UserListFilterField,
    filterValue?: string,
  ): UserListPage {
    const payload = this.unwrapResponse<UserListPage | UserItem[]>(response);
    const normalizedFilterValue = filterValue?.trim();

    if (Array.isArray(payload)) {
      const filteredPayload = this.filterUsers(payload, filterField, normalizedFilterValue);
      const totalItems = filteredPayload.length;
      const totalPages = Math.max(1, Math.ceil(totalItems / requestedPageSize));
      const page = Math.min(Math.max(1, requestedPage), totalPages);
      const start = (page - 1) * requestedPageSize;
      return {
        items: filteredPayload.slice(start, start + requestedPageSize),
        page,
        pageSize: requestedPageSize,
        totalItems,
        totalPages,
      };
    }

    const items = this.filterUsers(Array.isArray(payload?.items) ? payload.items : [], filterField, normalizedFilterValue);
    const totalItems = normalizedFilterValue && filterField
      ? items.length
      : (Number(payload?.totalItems) || 0);
    const totalPages = normalizedFilterValue && filterField
      ? Math.max(1, Math.ceil(totalItems / (Number(payload?.pageSize) || requestedPageSize)))
      : (Number(payload?.totalPages) || 1);

    return {
      items,
      page: Number(payload?.page) || requestedPage,
      pageSize: Number(payload?.pageSize) || requestedPageSize,
      totalItems,
      totalPages,
    };
  }

  private filterUsers(users: UserItem[], filterField?: UserListFilterField, filterValue?: string): UserItem[] {
    if (!filterField || !filterValue) {
      return users;
    }

    if (filterField === 'email') {
      const normalizedEmail = filterValue.toLowerCase();
      return users.filter((user) => (user.email || '').toLowerCase() === normalizedEmail);
    }

    if (filterField === 'company') {
      const normalizedCompany = filterValue.toLowerCase();
      return users.filter((user) => (user.company || '').toLowerCase() === normalizedCompany);
    }

    if (filterField === 'role') {
      const normalizedRole = filterValue.toLowerCase();
      return users.filter((user) => (user.role || '').toLowerCase() === normalizedRole);
    }

    if (filterField === 'status') {
      const normalizedStatus = filterValue.toLowerCase();
      return users.filter((user) => {
        const userStatus = user.disabled ? 'suspended' : 'active';
        return userStatus === normalizedStatus;
      });
    }

    if (filterField === 'profileType') {
      const normalizedProfileType = filterValue.toLowerCase();
      return users.filter((user) => (user.profileType || '').toLowerCase() === normalizedProfileType);
    }

    return users.filter((user) => (user.id || '') === filterValue);
  }

  getUser(token: string, email: string): Observable<UserItem> {
    return this.http
      .get(`${clearScapeAPIBaseUrl}/admin/users/${encodeURIComponent(email)}`, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<UserItem>(response)));
  }

  updateUser(token: string, id: string, payload: Partial<UserItem>): Observable<UserItem> {
    return this.http
      .patch(`${clearScapeAPIBaseUrl}/admin/users/${encodeURIComponent(id)}`, payload, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<UserItem>(response)));
  }

  userMaintenance(token: string, user: string, type: UserMaintenanceType, note?: string): Observable<any> {
    return this.http
      .post(`${clearScapeAPIBaseUrl}/admin/users/maintenance`, { user, type, note }, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<any>(response)));
  }

  getUserNotebooks(token: string, email: string): Observable<EnvironmentNotebooks[]> {
    return this.http
      .get(`${clearScapeAPIBaseUrl}/admin/users/${encodeURIComponent(email)}/notebooks`, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<EnvironmentNotebooks[]>(response) || []));
  }

  getUserUsage(token: string, email: string): Observable<UserUsage> {
    return this.http
      .get(`${clearScapeAPIBaseUrl}/admin/users/${encodeURIComponent(email)}/usage`, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<UserUsage>(response)));
  }

  listEnvironments(
    token: string,
    options?: { page?: number; pageSize?: number; filterField?: EnvironmentListFilterField; filterValue?: string },
  ): Observable<EnvironmentListPage> {
    let params = new HttpParams();
    const page = options?.page ?? 1;
    const pageSize = options?.pageSize ?? 20;
    const filterField = options?.filterField;
    const filterValue = options?.filterValue?.trim();

    if (typeof page === 'number' && Number.isFinite(page)) {
      params = params.set('page', String(page));
    }

    if (typeof pageSize === 'number' && Number.isFinite(pageSize)) {
      params = params.set('pageSize', String(pageSize));
    }

    if (filterField && filterValue) {
      params = params.set('filterField', filterField);
      params = params.set('filterValue', filterValue);
    }

    return this.http
      .get(`${clearScapeAPIBaseUrl}/admin/environments`, { headers: this.authHeaders(token), params })
      .pipe(map((response) => this.normalizeEnvironmentListResponse(response, page, pageSize, filterField, filterValue)));
  }

  private normalizeEnvironmentListResponse(
    response: any,
    requestedPage: number,
    requestedPageSize: number,
    filterField?: EnvironmentListFilterField,
    filterValue?: string,
  ): EnvironmentListPage {
    const payload = this.unwrapResponse<EnvironmentListPage | AdminEnvironment[]>(response);
    const normalizedFilterValue = filterValue?.trim();

    if (Array.isArray(payload)) {
      const filteredPayload = this.filterEnvironments(payload, filterField, normalizedFilterValue);
      const totalItems = filteredPayload.length;
      const totalPages = Math.max(1, Math.ceil(totalItems / requestedPageSize));
      const page = Math.min(Math.max(1, requestedPage), totalPages);
      const start = (page - 1) * requestedPageSize;
      return {
        items: filteredPayload.slice(start, start + requestedPageSize),
        page,
        pageSize: requestedPageSize,
        totalItems,
        totalPages,
      };
    }

    const items = this.filterEnvironments(Array.isArray(payload?.items) ? payload.items : [], filterField, normalizedFilterValue);
    const totalItems = normalizedFilterValue && filterField
      ? items.length
      : (Number(payload?.totalItems) || 0);
    const totalPages = normalizedFilterValue && filterField
      ? Math.max(1, Math.ceil(totalItems / (Number(payload?.pageSize) || requestedPageSize)))
      : (Number(payload?.totalPages) || 1);

    return {
      items,
      page: Number(payload?.page) || requestedPage,
      pageSize: Number(payload?.pageSize) || requestedPageSize,
      totalItems,
      totalPages,
    };
  }

  private filterEnvironments(
    environments: AdminEnvironment[],
    filterField?: EnvironmentListFilterField,
    filterValue?: string,
  ): AdminEnvironment[] {
    if (!filterField || !filterValue) {
      return environments;
    }

    const normalizedValue = filterValue.toLowerCase();

    if (filterField === 'name') {
      return environments.filter((environment) => (environment.name || '').toLowerCase() === normalizedValue);
    }

    if (filterField === 'owner') {
      return environments.filter((environment) => (environment.owner || '').toLowerCase() === normalizedValue);
    }

    if (filterField === 'status') {
      return environments.filter((environment) => (environment.state || '').toLowerCase() === normalizedValue);
    }

    return environments.filter((environment) => (environment.region || '').toLowerCase() === normalizedValue);
  }

  modifyEnvironments(
    token: string,
    operations: Array<{ operation: EnvironmentOperation; environment: string; owner: string }>,
  ): Observable<EnvironmentOperationStatus[]> {
    return this.http
      .post(
        `${clearScapeAPIBaseUrl}/admin/environments/modify`,
        operations,
        { headers: this.authHeaders(token) },
      )
      .pipe(map((response) => this.unwrapResponse<EnvironmentOperationStatus[]>(response) || []));
  }

  describeEnvironment(token: string, owner: string, name: string): Observable<EnvironmentDetail> {
    return this.http
      .get(
        `${clearScapeAPIBaseUrl}/admin/environments/user/${encodeURIComponent(owner)}/environment/${encodeURIComponent(name)}`,
        { headers: this.authHeaders(token) },
      )
      .pipe(map((response) => this.unwrapResponse<EnvironmentDetail>(response)));
  }

  getEnvironmentEvents(token: string, owner: string, name: string): Observable<EnvironmentEvent[]> {
    return this.http
      .get(
        `${clearScapeAPIBaseUrl}/admin/environments/user/${encodeURIComponent(owner)}/environment/${encodeURIComponent(name)}/events`,
        { headers: this.authHeaders(token) },
      )
      .pipe(map((response) => this.unwrapResponse<EnvironmentEvent[]>(response) || []));
  }

  listPrismaAlerts(token: string, lookbackDays?: number): Observable<PrismaAlert[]> {
    const params: Record<string, string> = {};
    if (typeof lookbackDays === 'number' && Number.isFinite(lookbackDays)) {
      params['lookbackDays'] = String(lookbackDays);
    }
    return this.http
      .get(`${clearScapeAPIBaseUrl}/admin/alerts/prisma`, { headers: this.authHeaders(token), params })
      .pipe(map((response) => this.unwrapResponse<PrismaAlert[]>(response) || []));
  }

  deletePrismaAlert(token: string, id: string): Observable<any> {
    return this.http
      .delete(`${clearScapeAPIBaseUrl}/admin/alerts/prisma/${encodeURIComponent(id)}`, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<any>(response)));
  }

  closePrismaAlert(token: string, id: string): Observable<any> {
    return this.http
      .post(`${clearScapeAPIBaseUrl}/admin/alerts/prisma/${encodeURIComponent(id)}/close`, {}, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<any>(response)));
  }

  markPrismaAlertSuspended(token: string, id: string): Observable<any> {
    return this.http
      .post(`${clearScapeAPIBaseUrl}/admin/alerts/prisma/${encodeURIComponent(id)}/mark-suspended`, {}, { headers: this.authHeaders(token) })
      .pipe(map((response) => this.unwrapResponse<any>(response)));
  }

  generatePrismaAlertSuggestion(token: string, alert: PrismaAlert, force = false): Observable<{ id: string; suggestion: string }> {
    return this.http
      .post(
        `${clearScapeAPIBaseUrl}/admin/alerts/prisma/${encodeURIComponent(alert.id)}/suggestion?force=${force}`,
        { alert, force },
        { headers: this.authHeaders(token) },
      )
      .pipe(map((response) => this.unwrapResponse<{ id: string; suggestion: string }>(response)));
  }
}
